// Hook custom pour charger les histoires depuis l'API avec états loading/error/data.
// Usage :
//   const { data, loading, error, reload } = useStories({ category: 'histoires-de-vie', limit: 50 });
import { useCallback, useEffect, useRef, useState } from 'react';
import { StoriesAPI } from '../api';

export function useStories(params = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const paramsKey = JSON.stringify(params);
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const parsed = JSON.parse(paramsKey);
      const result = await StoriesAPI.list(parsed);
      if (!ctrl.signal.aborted) setData(Array.isArray(result) ? result : []);
    } catch (err) {
      if (err.name !== 'AbortError' && !ctrl.signal.aborted) setError(err);
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, [paramsKey]);

  useEffect(() => {
    load();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [load]);

  return { data, loading, error, reload: load };
}

// Hook pour un article unique (par slug)
export function useStoryBySlug(slug) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    StoriesAPI.getBySlug(slug)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  return { data, loading, error };
}

// Adaptateur : convertit la forme MongoDB (snake_case) en forme utilisée par les composants
// existants (camelCase — categorySlug, readTime), sans dupliquer l'objet inutilement.
export function normaliseStory(s) {
  if (!s) return s;
  return {
    ...s,
    categorySlug: s.category_slug || s.categorySlug,
    readTime: s.read_time || s.readTime,
  };
}

export function normaliseStories(list) {
  return (list || []).map(normaliseStory);
}
